/**
 * Helper composable for performing API mutations (POST, PUT, PATCH, DELETE).
 *
 * Unlike `useAsyncData`, mutations are not cached and are executed on demand.
 * Returns reactive state for loading, error, and the execute function.
 */
export function useMutation<TData, TVariables = void>(fetcher: (vars: TVariables) => Promise<TData>) {
  const data = ref<TData | null>(null) as Ref<TData | null>
  const error = ref<unknown>(null)
  const pending = ref(false)

  async function execute(vars: TVariables): Promise<TData | null> {
    pending.value = true
    error.value = null

    try {
      const result = await fetcher(vars)
      data.value = result
      return result
    } catch (e) {
      error.value = e
      return null
    } finally {
      pending.value = false
    }
  }

  return {
    data: readonly(data),
    error: readonly(error),
    pending: readonly(pending),
    execute,
  }
}
